import React, { useEffect, useState, useRef } from "react";
import { Calendar, Badge, Modal, Button, Form, Input, Select, DatePicker, TimePicker, Row, Col, Card, message, Typography, Popconfirm, Tooltip } from "antd";
import { useAuth } from "../Context";
import dayjs from "dayjs";
import 'dayjs/locale/es';
import locale from 'antd/es/date-picker/locale/es_ES';
import { EditOutlined, DeleteOutlined, FieldTimeOutlined, FileAddOutlined } from '@ant-design/icons';
import EmpleadosSelect from "../empleados/EmpleadosSelect";
import * as agendaService from "../../servicios/agenda/servicios";
import { useAuditoria } from "../../hooks/useAuditoria";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Evento {
  id: string;
  titulo: string;
  descripcion?: string;
  fecha: string;
  horaInicio?: string;
  horaFin?: string;
  ubicacion?: string;
  participantes: string[];
  creadorId: string;
  color?: string;
  tieneMinuta?: boolean;
  periodoId?: string; // Identificador del período (YYYY-MM)
}

// Dejamos este comentario para futuras referencias
// El formato de fecha para los eventos debe ser: YYYY-MM-DD

const AgendaCalendario: React.FC = () => {
  const { sucursal, token, currentUser } = useAuth();
  const { auditar } = useAuditoria();
  
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("Crear Evento");
  const [editingEvent, setEditingEvent] = useState<Evento | null>(null);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [minutaModalVisible, setMinutaModalVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [minutaForm] = Form.useForm();

  // Colores disponibles para eventos
  const eventColors = [
    { value: '#1890ff', label: 'Azul (Default)' },
    { value: '#52c41a', label: 'Verde' },
    { value: '#faad14', label: 'Amarillo' },
    { value: '#f5222d', label: 'Rojo' },
    { value: '#722ed1', label: 'Púrpura' },
    { value: '#eb2f96', label: 'Rosa' },
    { value: '#fa8c16', label: 'Naranja' },
  ];

  // Fecha actual para controlar qué mes se está visualizando
  const [currentViewDate, setCurrentViewDate] = useState<dayjs.Dayjs>(dayjs());

  // Actualiza el estado cuando cambia el mes visualizado
  const handlePanelChange = (date: dayjs.Dayjs) => {
    const nuevoPeriodo = date.format('YYYY-MM');
    
    // Registrar el tiempo del cambio de panel
    lastPanelChangeTime.current = Date.now();
    setIsPanelChanging(true);
    setCurrentViewDate(date);
    
    // Cargar eventos del nuevo período inmediatamente
    if (currentUser?.uid && sucursal && token) {
      cargarEventosPeriodo(nuevoPeriodo).then(eventos => {
        setEventos(eventos);
      }).catch(error => {
        console.error(`💥 Error cargando eventos para ${nuevoPeriodo}:`, error);
      });
    }
    
    // Restablecer la bandera después de un retraso
    setTimeout(() => {
      setIsPanelChanging(false);
    }, 300);
  };

  // Control para evitar cargas duplicadas
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [isPanelChanging, setIsPanelChanging] = useState<boolean>(false);
  const lastPanelChangeTime = useRef<number>(0);
  
  // Inicializar el tiempo de cambio de panel
  useEffect(() => {
    lastPanelChangeTime.current = Date.now();
  }, []);
  
  useEffect(() => {
    if (currentUser?.uid && sucursal && token) {
      // Cargar eventos cuando cambia el mes visualizado o los datos de autenticación
      cargarEventos();
    }
  }, [currentUser?.uid, sucursal, token, currentViewDate.format('YYYY-MM')]);

  // Obtiene el periodoId para el mes actual
  const getPeriodoIdForCurrentView = (): string => {
    const year = currentViewDate.year();
    const month = currentViewDate.month() + 1; // Los meses en dayjs van de 0 a 11
    return `${year}-${month < 10 ? '0' + month : month}`;
  };

  // Función para obtener solo el período actual (sin períodos adyacentes)
  const getCurrentPeriod = (): string => {
    return getPeriodoIdForCurrentView();
  };

  // Función para cargar eventos de un período específico
  const cargarEventosPeriodo = async (periodoId: string): Promise<Evento[]> => {
    try {
      const eventosDelPeriodo = await agendaService.getEventosPorUsuario(
        currentUser?.uid || '', 
        sucursal || '', 
        token || '',
        periodoId
      );
      
      if (eventosDelPeriodo.length > 0) {
        // Validar y normalizar los eventos
        const eventosNormalizados = eventosDelPeriodo.map(evento => {
          let eventoNormalizado = { ...evento };
          
          // Normalizar la fecha
          if (evento.fecha && typeof evento.fecha === 'string') {
            if (evento.fecha.length > 10) {
              eventoNormalizado.fecha = evento.fecha.substring(0, 10);
            }
          } else {
            eventoNormalizado.fecha = periodoId + '-01'; // Fecha por defecto
          }
          
          // Asegurar que tenga periodoId
          if (!eventoNormalizado.periodoId) {
            eventoNormalizado.periodoId = periodoId;
          }
          
          return eventoNormalizado;
        });
        
        return eventosNormalizados;
      }
      
      return [];
    } catch (error) {
      console.error(`Error cargando eventos del período ${periodoId}:`, error);
      return [];
    }
  };

  /**
   * Carga un evento específico por ID y asegura que se muestre en el calendario
   */
  /**
   * Carga un evento específico por ID
   * @param {string} eventoId - ID del evento a cargar
   * @param {string} periodoId - Periodo (YYYY-MM) al que pertenece el evento
   * @returns {Promise<Evento|null>} - El evento cargado o null si no se encontró
   */
  const cargarEventoEspecifico = async (eventoId: string, periodoId: string) => {
    try {
      // Verificar si ya tenemos el evento en memoria
      const eventoExistente = eventos.find(e => e.id === eventoId);
      if (eventoExistente) {
        return eventoExistente;
      }
      
      // Si no existe, lo cargamos de la API
      const evento = await agendaService.getEventoPorId(
        sucursal || '',
        token || '',
        periodoId,
        eventoId
      );
      
      if (evento) {
        let eventoNormalizado = { ...evento };
        
        // Normalizar la fecha para todos los eventos
        if (evento.fecha && typeof evento.fecha === 'string') {
          // Asegurar que la fecha tenga el formato YYYY-MM-DD sin hora
          if (evento.fecha.length > 10) {
            eventoNormalizado.fecha = evento.fecha.substring(0, 10);
          }
        }
        
        // Añadir a eventos existentes
        setEventos(eventosActuales => {
          // Remover el evento si ya existe para evitar duplicados
          const eventosFiltrados = eventosActuales.filter(e => e.id !== eventoId);
          // Añadir el evento actualizado
          return [...eventosFiltrados, eventoNormalizado];
        });
        
        return eventoNormalizado;
      } else {
        return null;
      }
    } catch (error) {
      console.error(`Error al cargar evento específico ${eventoId}:`, error);
      message.error("Error al cargar el evento");
      return null;
    }
  };
  
  /**
   * Carga los eventos para el período actual visible (UNA SOLA CONSULTA)
   */
  const cargarEventos = async () => {
    if (!currentUser?.uid || !sucursal || !token) {
      return;
    }

    setLoading(true);
    try {
      // Obtener solo el período actual
      const periodoActual = getCurrentPeriod();
      
      // Control de cache para evitar múltiples cargas del mismo período
      const cacheKey = `agenda_${periodoActual}_${currentUser?.uid}`;
      const ultimaCarga = sessionStorage.getItem(cacheKey);
      const ahora = new Date().getTime();
      
      // Evitar recargas en menos de 10 segundos para el mismo período
      if (ultimaCarga && (ahora - parseInt(ultimaCarga)) < 10000) {
        setLoading(false);
        return;
      }
      
      // Registrar esta carga
      sessionStorage.setItem(cacheKey, ahora.toString());
      
      // Hacer UNA SOLA llamada para el período actual
      const eventosDelPeriodo = await cargarEventosPeriodo(periodoActual);
      
      // Actualizar el estado con los eventos del período actual
      setEventos(eventosDelPeriodo);
      
    } catch (error) {
      console.error("💥 Error al cargar eventos:", error);
      message.error("Error al cargar los eventos de la agenda");
    } finally {
      setLoading(false);
    }
  };

  const dateCellRender = (value: dayjs.Dayjs) => {
    // Normalizar la fecha de la celda (sin hora/minuto/segundo)
    const cellDate = value.startOf('day');
    const formattedDate = cellDate.format('YYYY-MM-DD');
    
    // Filtrar eventos del día de manera más simple y efectiva
    const listData = eventos.filter(evento => {
      // Verificación básica
      if (!evento || !evento.fecha) {
        return false;
      }
      
      try {
        // Normalizar la fecha del evento para asegurar formato YYYY-MM-DD
        let fechaEvento = '';
        if (typeof evento.fecha === 'string') {
          fechaEvento = evento.fecha.substring(0, 10); // Tomar solo YYYY-MM-DD
        } else {
          return false;
        }
        
        // Comparación directa de strings en formato YYYY-MM-DD
        return fechaEvento === formattedDate;
      } catch (error) {
        console.error(`Error al procesar fecha del evento:`, evento, error);
        return false;
      }
    });
    
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {/* Eventos normales de la lista */}
        {listData.map(item => {
          return (
            <li key={item.id} style={{ 
                marginBottom: 3, 
                padding: '0'
              }}>
              <Badge 
                color={item.color || '#1890ff'} 
                text={
                  <Tooltip title={
                    <>
                      <div><strong>{item.titulo}</strong></div>
                      {item.descripcion && <div>{item.descripcion}</div>}
                      <div>Fecha: {typeof item.fecha === 'string' ? item.fecha.substring(0, 10) : 'Fecha inválida'}</div>
                      {item.horaInicio && <div>Hora: {item.horaInicio}</div>}
                    </>
                  }>
                    <div 
                      style={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        fontSize: '0.85em'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditEvent(item);
                      }}
                    >
                      {item.horaInicio ? `${item.horaInicio.substring(0, 5)} - ` : ''}{item.titulo}
                    </div>
                  </Tooltip>
                } 
              />
            </li>
          );
        })}
      </ul>
    );
  };

  // Se ejecuta cuando el usuario selecciona un día en el calendario
  const handleSelect = (value: dayjs.Dayjs) => {
    const timeSinceLastPanelChange = Date.now() - lastPanelChangeTime.current;
    const selectedMonth = value.format('YYYY-MM');
    const currentMonth = currentViewDate.format('YYYY-MM');
    
    // No abrir el modal si:
    // 1. Estamos en medio de un cambio de panel (bandera activa)
    // 2. Ha pasado menos de 600ms desde el último cambio de panel
    // 3. El mes seleccionado no coincide con el mes actual visualizado
    if (isPanelChanging || timeSinceLastPanelChange < 600) {
      return;
    }
    
    // Verificación adicional: solo abrir modal si el mes coincide
    if (selectedMonth !== currentMonth) {
      return;
    }
    
    setSelectedDate(value);
    setModalTitle("Crear Evento");
    setEditingEvent(null);
    
    form.resetFields();
    form.setFieldsValue({
      fecha: value,
      color: '#1890ff',
    });
    
    setModalVisible(true);
  };

  const handleEditEvent = (evento: Evento) => {
    setEditingEvent(evento);
    setModalTitle("Editar Evento");
    
    form.resetFields();
    form.setFieldsValue({
      titulo: evento.titulo,
      descripcion: evento.descripcion,
      fecha: dayjs(evento.fecha),
      horaInicio: evento.horaInicio ? dayjs(evento.horaInicio, 'HH:mm') : undefined,
      horaFin: evento.horaFin ? dayjs(evento.horaFin, 'HH:mm') : undefined,
      ubicacion: evento.ubicacion,
      participantes: evento.participantes,
      color: evento.color || '#1890ff',
    });
    
    setModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      
      // Calcular el periodoId para la fecha seleccionada
      const fecha = values.fecha;
      const periodoId = `${fecha.year()}-${(fecha.month() + 1) < 10 ? '0' + (fecha.month() + 1) : fecha.month() + 1}`;
      
      const eventoData = {
        titulo: values.titulo,
        descripcion: values.descripcion,
        fecha: values.fecha.format('YYYY-MM-DD'),
        horaInicio: values.horaInicio ? values.horaInicio.format('HH:mm') : undefined,
        horaFin: values.horaFin ? values.horaFin.format('HH:mm') : undefined,
        ubicacion: values.ubicacion,
        participantes: values.participantes,
        color: values.color,
        sucursal: sucursal || '',
        periodoId: periodoId,
      };
      
      let result;
      
      if (editingEvent) {
        // Verificar si cambió el periodo (mes)
        const periodoIdAnterior = editingEvent.periodoId;
        const esNuevoPeriodo = periodoId !== periodoIdAnterior;
        
        // Actualizar evento existente
        result = await agendaService.actualizarEvento(token || '', {
          eventoId: editingEvent.id,
          ...eventoData,
          periodoIdAnterior: esNuevoPeriodo ? periodoIdAnterior : undefined
        });
        
        if (result.success) {
          message.success("Evento actualizado correctamente");
          auditar('editar', 'agenda', { 
            evento_id: editingEvent.id,
            titulo: values.titulo,
            cambio_periodo: esNuevoPeriodo
          }).catch(err => {
            console.error("Error al auditar actualización de evento:", err);
          });
        } else {
          message.error(result.message || "Error al actualizar el evento");
        }
      } else {
        // Crear nuevo evento
        result = await agendaService.crearEvento(token || '', {
          ...eventoData,
          creadorId: currentUser?.uid || '',
        });
        
        if (result.success) {
          message.success("Evento creado correctamente");
          auditar('crear', 'agenda', { 
            titulo: values.titulo,
            participantes: values.participantes.length,
            periodoId: periodoId
          }).catch(err => {
            console.error("Error al auditar creación de evento:", err);
          });
        } else {
          message.error(result.message || "Error al crear el evento");
        }
      }
      
      if (result.success) {
        setModalVisible(false);
        form.resetFields();
        cargarEventos();
      }
    } catch (error) {
      console.error("Error en el formulario:", error);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
  };

  const handleDelete = async (evento: Evento) => {
    try {
      const result = await agendaService.eliminarEvento(
        token || '', 
        evento.id,
        sucursal || '',
        evento.periodoId // Incluimos el periodoId para la eliminación
      );
      
      if (result.success) {
        message.success("Evento eliminado correctamente");
        cargarEventos();
        auditar('eliminar', 'agenda', { 
          evento_id: evento.id,
          titulo: evento.titulo,
          periodoId: evento.periodoId
        }).catch(err => {
          console.error("Error al auditar eliminación de evento:", err);
        });
      } else {
        message.error(result.message || "Error al eliminar el evento");
      }
    } catch (error) {
      console.error("Error al eliminar evento:", error);
      message.error("Error al eliminar el evento");
    }
  };

  const handleAgregarMinuta = (evento: Evento) => {
    setEditingEvent(evento);
    minutaForm.resetFields();
    setMinutaModalVisible(true);
  };

  const handleGuardarMinuta = async () => {
    try {
      if (!editingEvent) return;
      
      const values = await minutaForm.validateFields();
      
      const result = await agendaService.agregarMinuta(token || '', {
        eventoId: editingEvent.id,
        resumen: values.resumen,
        sucursal: sucursal || '',
        periodoId: editingEvent.periodoId // Incluimos el periodoId para la minuta
      });
      
      if (result.success) {
        message.success("Minuta guardada correctamente");
        setMinutaModalVisible(false);
        cargarEventos();
        auditar('crear_minuta', 'agenda', { 
          evento_id: editingEvent.id,
          titulo_evento: editingEvent.titulo,
          periodoId: editingEvent.periodoId
        }).catch(err => {
          console.error("Error al auditar creación de minuta:", err);
        });
      } else {
        message.error(result.message || "Error al guardar la minuta");
      }
    } catch (error) {
      console.error("Error al guardar minuta:", error);
    }
  };

  const handleCancelarMinuta = () => {
    setMinutaModalVisible(false);
  };

  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={2}>Agenda</Title>
        </Col>
      </Row>
      
      <Card loading={loading}>
        <Calendar 
          dateCellRender={dateCellRender} 
          onSelect={handleSelect}
          onPanelChange={handlePanelChange}
          mode="month"
          value={currentViewDate}
        />
      </Card>
      
      {/* Modal para crear/editar eventos */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose={true}
        width={700}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancelar
          </Button>,
          editingEvent && (
            <Popconfirm
              key="delete"
              title="¿Está seguro de eliminar este evento?"
              onConfirm={() => {
                handleDelete(editingEvent);
                setModalVisible(false);
              }}
              okText="Sí"
              cancelText="No"
            >
              <Button danger icon={<DeleteOutlined />}>
                Eliminar
              </Button>
            </Popconfirm>
          ),
          editingEvent && !editingEvent.tieneMinuta && (
            <Button 
              key="minuta" 
              type="dashed" 
              icon={<FileAddOutlined />}
              onClick={() => {
                setModalVisible(false);
                handleAgregarMinuta(editingEvent);
              }}
            >
              Agregar Minuta
            </Button>
          ),
          <Button key="submit" type="primary" onClick={handleOk}>
            {editingEvent ? 'Actualizar' : 'Crear'}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="titulo"
            label="Título"
            rules={[{ required: true, message: 'Por favor ingrese el título del evento' }]}
          >
            <Input placeholder="Título del evento" />
          </Form.Item>
          
          <Form.Item
            name="descripcion"
            label="Descripción"
          >
            <TextArea rows={4} placeholder="Descripción del evento" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="fecha"
                label="Fecha"
                rules={[{ required: true, message: 'Por favor seleccione la fecha' }]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  format="DD/MM/YYYY" 
                  locale={locale}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="horaInicio"
                label="Hora de inicio"
              >
                <TimePicker 
                  style={{ width: '100%' }} 
                  format="HH:mm" 
                  placeholder="Seleccione hora"
                  minuteStep={15}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="horaFin"
                label="Hora de fin"
              >
                <TimePicker 
                  style={{ width: '100%' }} 
                  format="HH:mm" 
                  placeholder="Seleccione hora"
                  minuteStep={15}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="ubicacion"
            label="Ubicación"
          >
            <Input placeholder="Ubicación del evento" />
          </Form.Item>
          
          <Form.Item
            name="participantes"
            label="Participantes"
            rules={[{ required: true, message: 'Por favor seleccione al menos un participante' }]}
          >
            <EmpleadosSelect 
              mode="multiple" 
              onChange={(value: string[]) => form.setFieldsValue({ participantes: value })} 
              value={form.getFieldValue('participantes')}
            />
          </Form.Item>
          
          <Form.Item
            name="color"
            label="Color"
          >
            <Select options={eventColors} />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* Modal para agregar minuta */}
      <Modal
        title="Agregar Minuta"
        open={minutaModalVisible}
        onOk={handleGuardarMinuta}
        onCancel={handleCancelarMinuta}
        destroyOnClose={true}
      >
        <Form
          form={minutaForm}
          layout="vertical"
        >
          {editingEvent && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>Evento:</Text> {editingEvent.titulo}
              <br />
              <Text strong>Fecha:</Text> {dayjs(editingEvent.fecha).format('DD/MM/YYYY')}
            </div>
          )}
          
          <Form.Item
            name="resumen"
            label="Resumen de la reunión"
            rules={[{ required: true, message: 'Por favor ingrese el resumen de la reunión' }]}
          >
            <TextArea rows={8} placeholder="Escriba aquí el resumen, acuerdos y compromisos de la reunión..." />
          </Form.Item>
          
          {/* Se puede agregar aquí un componente para subir archivos si es necesario */}
        </Form>
      </Modal>
    </div>
  );
};

export default AgendaCalendario;
